import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Download, PaperPlaneTilt, ArrowClockwise, CurrencyEur, FileZip, Pencil, Check, X, Key, FilePdf, Printer, CaretDown, LockKeyOpen } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import PaperRecapImportView from "./PaperRecapImportView";
import TeleproAccountsHealthPanel from "./TeleproAccountsHealthPanel";
import AdminTeleproDashboardView from "./AdminTeleproDashboardView";
import AdminQualificationStatsPanel from "./AdminQualificationStatsPanel";
import AdminPerformancePanel from "./AdminPerformancePanel";
import AdminTrainingPanel from "./AdminTrainingPanel";

const PLAN_LABELS = {
  premium_promo:    { label: "Premium Promo",    sale: "1€",  monthly: "0,25€" },
  premium_ai_promo: { label: "Premium IA Promo", sale: "2€",  monthly: "0,50€" },
  premium:          { label: "Premium",          sale: "3€",  monthly: "2€" },
  premium_ai:       { label: "Premium IA",       sale: "6€",  monthly: "4€" },
};

export default function TeleprospectionPanel() {
  const [view, setView] = useState("team"); // team | lists | sales | commissions
  const [telepros, setTelepros] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTelepros = useCallback(async () => {
    try {
      const r = await api.get("/admin/telepro");
      setTelepros(r.data.items || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadTelepros(); }, [loadTelepros]);

  return (
    <div data-testid="admin-telepro" className="space-y-5">
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="label-uppercase mb-1">Téléprospection</div>
        <h2 className="font-display text-xl font-medium mb-1">Gestion des téléprospecteurs/trices</h2>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Créer ton équipe commerciale, générer leurs listes du jour (PDF + email auto), suivre les ventes et calculer les commissions.
        </p>
      </div>

      <DailyDistributionCheckCard/>

      <div className="flex gap-1 border-b border-border overflow-x-auto" data-testid="telepro-subnav">
        {[
          ["team", "Équipe"],
          ["accounts", "État des comptes"],
          ["qualification", "Refus & leads"],
          ["performance", "Performance"],
          ["training", "Formation"],
          ["view_dashboards", "Voir dashboards"],
          ["failures", "Inscriptions échouées"],
          ["lists", "Listes du jour"],
          ["sales", "Ventes"],
          ["commissions", "Commissions"],
          ["contracts", "Contrats signés"],
          ["paper_recap", "Import récap papier"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${view === key ? "border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`telepro-tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === "team" && <TeleproTeamView telepros={telepros} reload={loadTelepros} loading={loading} setLoading={setLoading}/>}
      {view === "accounts" && <TeleproAccountsHealthPanel />}
      {view === "qualification" && <AdminQualificationStatsPanel />}
      {view === "performance" && <AdminPerformancePanel telepros={telepros}/>}
      {view === "training" && <AdminTrainingPanel />}
      {view === "view_dashboards" && <AdminTeleproDashboardView />}
      {view === "failures" && <TeleproSignupFailuresView />}
      {view === "lists" && <TeleproListsView reload={loadTelepros}/>}
      {view === "sales" && <TeleproSalesView telepros={telepros}/>}
      {view === "commissions" && <TeleproCommissionsView telepros={telepros}/>}
      {view === "contracts" && <TeleproContractsView />}
      {view === "paper_recap" && <PaperRecapImportView telepros={telepros}/>}
    </div>
  );
}

// ---------- Daily distribution validation (VAGUE 18e) ----------
function DailyDistributionCheckCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/telepro/daily-distribution-check");
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!data) return null;

  const isClean = data.duplicate_count === 0 && data.total_prospects > 0;
  const isEmpty = data.total_prospects === 0;
  const bgClass = isEmpty
    ? "bg-muted/40 border-border"
    : isClean
    ? "bg-emerald-50 border-emerald-300 dark:bg-emerald-950/20"
    : "bg-rose-50 border-rose-300 dark:bg-rose-950/20";

  return (
    <div
      className={`border rounded-sm p-4 ${bgClass}`}
      data-testid="daily-distribution-check"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-lg ${isClean ? "text-emerald-700" : isEmpty ? "text-muted-foreground" : "text-rose-700"}`}>
              {isClean ? "✓" : isEmpty ? "◯" : "⚠"}
            </span>
            <h3 className={`font-display text-base font-semibold ${isClean ? "text-emerald-900 dark:text-emerald-200" : isEmpty ? "text-muted-foreground" : "text-rose-900 dark:text-rose-200"}`}>
              Validation quotidienne — {new Date(data.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            </h3>
          </div>
          {isEmpty ? (
            <p className="text-sm text-muted-foreground">
              Aucune liste générée aujourd'hui. Les listes sont produites automatiquement à 07h UTC.
            </p>
          ) : isClean ? (
            <p className="text-sm text-emerald-800 dark:text-emerald-300">
              <strong>{data.total_prospects} prospects</strong> distribués sur <strong>{data.total_lists} télépros</strong> — <strong>0 doublon</strong> détecté. Chaque prospect apparaît dans une seule liste ✅
            </p>
          ) : (
            <p className="text-sm text-rose-800 dark:text-rose-300">
              <strong>{data.duplicate_count} doublons</strong> détectés sur {data.total_prospects} prospects — {data.total_lists} listes.
            </p>
          )}
        </div>
        <button
          onClick={load}
          className="btn-secondary btn-sm shrink-0"
          data-testid="distribution-check-refresh"
        >
          <ArrowClockwise size={14}/>
          Rafraîchir
        </button>
      </div>

      {!isEmpty && data.per_telepro?.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">
            Détail par télépro ({data.per_telepro.length})
          </summary>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
            {data.per_telepro.map((t) => (
              <div key={t.code} className="flex items-center justify-between bg-background/70 border border-border rounded-sm px-2 py-1">
                <span className="font-medium truncate mr-1" title={t.name}>{t.code}</span>
                <span className="tabular-nums text-muted-foreground">{t.count}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {data.duplicates?.length > 0 && (
        <div className="mt-3 border-t border-rose-300 pt-3">
          <div className="text-xs font-semibold text-rose-800 mb-2">Prospects en double :</div>
          <div className="space-y-1 text-xs">
            {data.duplicates.map((d) => (
              <div key={d.biz_id} className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium">{d.name}</span>
                <span className="text-muted-foreground">({d.city} — {d.phone})</span>
                <span className="text-rose-700 font-mono">→ {d.telepro_codes.join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Signup failures panel (VAGUE 17) ----------
function TeleproSignupFailuresView() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [onlyUnresolved, setOnlyUnresolved] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/telepro/signup-failures?limit=200&only_unresolved=${onlyUnresolved}`);
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      toast.error("Impossible de charger les échecs");
    } finally {
      setLoading(false);
    }
  }, [onlyUnresolved]);

  useEffect(() => { load(); }, [load]);

  const REASON_LABELS = {
    accept_terms_missing: "Conditions non acceptées",
    invalid_phone: "Téléphone invalide",
    invalid_iban: "IBAN invalide",
    invalid_bic: "BIC/SWIFT invalide",
    invalid_siret_status: "Statut SIRET non défini",
    invalid_business_id: "Identifiant entreprise invalide",
    email_already_exists: "Email déjà enregistré",
    rate_limited_ip: "Trop de tentatives depuis cette IP",
    pydantic_422: "Champs manquants ou format incorrect",
  };

  return (
    <div className="space-y-4" data-testid="signup-failures-panel">
      <div className="bg-card border border-border rounded-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-medium">Inscriptions abandonnées / échouées</h3>
            <p className="text-xs text-muted-foreground max-w-2xl mt-1">
              Chaque tentative d'inscription rejetée (IBAN invalide, SIRET manquant, champ mal saisi…) est loggée ici avec le nom, l'email et le téléphone du candidat. Contacte-les pour les rattraper avant qu'ils abandonnent définitivement.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={onlyUnresolved}
                onChange={(e) => setOnlyUnresolved(e.target.checked)}
                data-testid="failures-only-unresolved"
              />
              Non résolus uniquement
            </label>
            <button
              onClick={load}
              className="btn-secondary btn-sm"
              data-testid="failures-refresh"
            >
              <ArrowClockwise size={16}/>
              Rafraîchir
            </button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          {loading ? "Chargement…" : `${rows.length} affichés · ${total} au total`}
        </div>
      </div>

      {rows.length === 0 && !loading && (
        <div className="bg-card border border-border rounded-sm p-8 text-center text-sm text-muted-foreground">
          Aucune inscription échouée{onlyUnresolved ? " non résolue" : ""} pour l'instant. 🎉
        </div>
      )}

      {rows.map((r, idx) => (
        <div key={idx} className="bg-card border border-border rounded-sm p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-medium">
                {r.first_name} {r.last_name}
                {r.resolved && <span className="ml-2 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">Résolu</span>}
              </div>
              <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                <div>📧 <a href={`mailto:${r.email}`} className="underline">{r.email || "—"}</a></div>
                <div>📞 <a href={`tel:${r.phone}`} className="underline">{r.phone || "—"}</a></div>
                {r.city && <div>📍 {r.city} {r.country && `(${r.country})`}</div>}
                {r.iban_masked && <div className="font-mono text-xs">IBAN : {r.iban_masked}</div>}
                {r.siret_number_masked && <div className="font-mono text-xs">ID entreprise : {r.siret_number_masked}</div>}
              </div>
            </div>
            <div className="text-right text-xs">
              <div className="inline-block px-2 py-1 bg-orange-50 text-orange-800 rounded-sm font-medium">
                {REASON_LABELS[r.reason] || r.reason}
              </div>
              <div className="mt-1 text-muted-foreground">
                {r.created_at ? new Date(r.created_at).toLocaleString("fr-FR") : "—"}
              </div>
              <div className="mt-1 text-muted-foreground">IP : {r.ip || "—"}</div>
            </div>
          </div>
          {r.error_message && (
            <div className="mt-3 text-xs bg-muted/50 rounded-sm p-2 font-mono">
              {r.error_message}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- Signed contracts panel ----------
function TeleproContractsView() {
  const [rows, setRows] = useState([]);
  const [currentVersion, setCurrentVersion] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/telepro/contracts");
      setRows(r.data.items || []);
      setCurrentVersion(r.data.current_version || "");
    } catch (e) {
      toast.error("Impossible de charger les contrats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadOne = async (telepro_id, code) => {
    try {
      const res = await api.get(`/admin/telepro/${telepro_id}/contract.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrat-${code || telepro_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const downloadAllZip = async () => {
    try {
      const res = await api.get("/admin/telepro/contracts.zip", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "contrats-partenariat-signes.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aucun contrat signé pour le moment");
    }
  };

  const signed = rows.filter((r) => r.contract_signed_at);
  const unsigned = rows.filter((r) => !r.contract_signed_at);
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="admin-contracts-view">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-medium">Contrats partenariat</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Version courante : <code className="bg-accent px-1 rounded font-mono">{currentVersion}</code>
            {" · "}{signed.length} signé{signed.length > 1 ? "s" : ""} · {unsigned.length} non signé{unsigned.length > 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={downloadAllZip}
          disabled={signed.length === 0}
          className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="admin-contracts-download-all"
        >
          <FileZip size={14} weight="bold"/> Tout télécharger (ZIP)
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Chargement…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="contracts-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left py-2 px-2">Code</th>
                <th className="text-left py-2 px-2">Téléprospecteur·trice</th>
                <th className="text-left py-2 px-2">Email</th>
                <th className="text-left py-2 px-2">Statut</th>
                <th className="text-left py-2 px-2">Signé le</th>
                <th className="text-left py-2 px-2">Ville</th>
                <th className="text-right py-2 px-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.telepro_id} className="border-b border-border/50 hover:bg-accent/30" data-testid={`contract-row-${r.code}`}>
                  <td className="py-2 px-2 font-mono text-xs"><b>{r.code}</b></td>
                  <td className="py-2 px-2">{r.first_name} {r.last_name}</td>
                  <td className="py-2 px-2 text-muted-foreground text-xs">{r.email}</td>
                  <td className="py-2 px-2">
                    {r.contract_signed_at ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-sm">
                        <Check size={11} weight="bold"/> Signé v{r.contract_version}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-secondary bg-secondary/10 px-2 py-0.5 rounded-sm">
                        <X size={11} weight="bold"/> En attente
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-xs">{fmt(r.contract_signed_at)}</td>
                  <td className="py-2 px-2 text-xs">{r.contract_signature_city || "—"}</td>
                  <td className="py-2 px-2 text-right">
                    <button
                      onClick={() => downloadOne(r.telepro_id, r.code)}
                      className="text-primary hover:text-[#0f3d29] inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 border border-border hover:border-primary rounded-sm"
                      title={r.contract_signed_at ? "Télécharger PDF signé" : "Télécharger PDF vierge"}
                      data-testid={`contract-download-${r.code}`}
                    >
                      <FilePdf size={12} weight="bold"/> PDF
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-muted-foreground text-sm">Aucun téléprospecteur dans la base.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Team view (CRUD) ----------
function TeleproTeamView({ telepros, reload, loading, setLoading }) {
  const [editing, setEditing] = useState(null); // null | "new" | {id...}

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce téléprospecteur/trice ? Ses ventes restent en historique.")) return;
    try {
      await api.delete(`/admin/telepro/${id}`);
      toast.success("Supprimé");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const toggleActive = async (tp) => {
    try {
      await api.patch(`/admin/telepro/${tp.id}`, { active: !tp.active });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const sendPortalLink = async (tp) => {
    if (!tp.email) {
      toast.error("Ce téléprospecteur n'a pas d'email enregistré.");
      return;
    }
    if (!window.confirm(`Envoyer le lien d'accès au portail à ${tp.first_name} ${tp.last_name} (${tp.email}) ?`)) return;
    try {
      const r = await api.post(`/admin/telepro/${tp.id}/send-portal-link`);
      toast.success(`Lien envoyé à ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    }
  };

  const resendWelcome = async (tp) => {
    if (!tp.email) {
      toast.error("Ce téléprospecteur n'a pas d'email enregistré.");
      return;
    }
    if (!window.confirm(
      `Renvoyer l'email de bienvenue complet à ${tp.first_name} ${tp.last_name} (${tp.email}) ?\n\nCet email inclut le magic-link (30 min), les prochaines étapes et le conseil Gmail pro. Une copie te sera aussi envoyée en tant qu'admin.`
    )) return;
    try {
      const r = await api.post(`/admin/telepro/${tp.id}/resend-welcome`);
      toast.success(`Email de bienvenue renvoyé à ${r.data.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    }
  };

  const resetPassword = async (tp) => {
    if (!window.confirm(
      `Réinitialiser le mot de passe personnel de ${tp.first_name} ${tp.last_name} (${tp.code}) ?\n\n` +
      `Son mot de passe actuel sera effacé. À sa prochaine connexion, l'app lui demandera automatiquement d'en créer un nouveau.`
    )) return;
    try {
      await api.post(`/admin/telepro/${tp.id}/reset-password`);
      toast.success(`Mot de passe de ${tp.code} réinitialisé. Il devra en créer un nouveau à sa prochaine connexion.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réinitialisation impossible");
    }
  };

  const downloadAsset = async (tp, kind) => {
    // kind: "tutorial" | "pitch"
    const path = kind === "pitch" ? `/admin/telepro/${tp.id}/pitch.pdf` : `/admin/telepro/${tp.id}/tutorial.pdf`;
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "pitch" ? `fiche-pitch-${tp.code}.pdf` : `guide-prospection-${tp.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Téléchargement impossible");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {telepros.length} téléprospecteur(s)/trice(s) · {telepros.filter(t => t.active).length} actif(s)
        </div>
        <button onClick={() => setEditing("new")} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-sm font-semibold inline-flex items-center gap-2" data-testid="telepro-add-btn">
          <Plus size={14} weight="bold"/> Ajouter
        </button>
      </div>

      {editing && <TeleproForm initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }}/>}

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-accent/20">
            <tr>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Nom</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2 text-right">Objectif/j</th>
              <th className="px-3 py-2 text-center">Statut</th>
              <th className="px-3 py-2 w-44"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {telepros.map(tp => (
              <tr key={tp.id} data-testid={`telepro-row-${tp.code}`}>
                <td className="px-3 py-2 font-mono text-primary text-xs font-bold">{tp.code}</td>
                <td className="px-3 py-2">{tp.first_name} {tp.last_name}</td>
                <td className="px-3 py-2 text-muted-foreground">{tp.email}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tp.daily_goal}</td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => toggleActive(tp)} className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${tp.active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {tp.active ? "Actif" : "Inactif"}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="inline-flex items-center gap-1 border border-border hover:border-foreground hover:bg-accent px-3 py-1.5 rounded-sm text-xs font-semibold" data-testid={`telepro-actions-${tp.code}`}>
                        Actions <CaretDown size={12}/>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => sendPortalLink(tp)} disabled={!tp.email || !tp.active} data-testid={`telepro-send-link-${tp.code}`}>
                        <Key size={14} className="mr-2"/> Envoyer le lien portail
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => resendWelcome(tp)} disabled={!tp.email} data-testid={`telepro-resend-welcome-${tp.code}`}>
                        <PaperPlaneTilt size={14} className="mr-2"/> Renvoyer email de bienvenue
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadAsset(tp, "tutorial")} data-testid={`telepro-tutorial-pdf-${tp.code}`}>
                        <FilePdf size={14} className="mr-2"/> Télécharger le guide PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadAsset(tp, "pitch")} data-testid={`telepro-pitch-pdf-${tp.code}`}>
                        <Printer size={14} className="mr-2"/> Fiche pitch A4
                      </DropdownMenuItem>
                      <DropdownMenuSeparator/>
                      <DropdownMenuItem onClick={() => resetPassword(tp)} data-testid={`telepro-reset-password-${tp.code}`}>
                        <LockKeyOpen size={14} className="mr-2"/> Réinitialiser le mot de passe
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setEditing(tp)} data-testid={`telepro-edit-${tp.code}`}>
                        <Pencil size={14} className="mr-2"/> Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => remove(tp.id)} className="text-destructive focus:text-destructive" data-testid={`telepro-delete-${tp.code}`}>
                        <Trash size={14} className="mr-2"/> Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
            {telepros.length === 0 && (
              <tr><td colSpan="6" className="px-3 py-10 text-center text-sm text-muted-foreground">Aucun téléprospecteur/trice. Ajoute le premier.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TeleproForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { first_name: "", last_name: "", email: "", phone: "", daily_goal: 60, active: true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Prénom, nom et email obligatoires");
      return;
    }
    setBusy(true);
    try {
      if (initial?.id) {
        await api.patch(`/admin/telepro/${initial.id}`, form);
      } else {
        await api.post("/admin/telepro", form);
      }
      toast.success("Enregistré");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-card border-2 border-primary/40 rounded-sm p-5">
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="Prénom *" value={form.first_name} onChange={v => setForm({...form, first_name: v})} testid="telepro-first-name"/>
        <Input label="Nom *" value={form.last_name} onChange={v => setForm({...form, last_name: v})} testid="telepro-last-name"/>
        <Input label="Email *" type="email" value={form.email} onChange={v => setForm({...form, email: v})} testid="telepro-email"/>
        <Input label="Téléphone" value={form.phone} onChange={v => setForm({...form, phone: v})} testid="telepro-phone"/>
        <Input label="Objectif journalier" type="number" value={form.daily_goal} onChange={v => setForm({...form, daily_goal: parseInt(v || 60)})} testid="telepro-daily-goal"/>
        <label className="flex items-center gap-2 text-sm pt-6">
          <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})}/>
          Actif (reçoit les listes)
        </label>
      </div>
      <div className="flex gap-2 mt-4 justify-end">
        <button onClick={onClose} className="px-4 py-2 text-sm hover:bg-accent rounded-sm">Annuler</button>
        <button onClick={save} disabled={busy} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2 text-sm font-semibold rounded-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="telepro-save-btn">
          <Check size={14}/> Enregistrer
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">{label}</div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
             className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none"
             data-testid={testid}/>
    </label>
  );
}

// ---------- Daily lists ----------
function TeleproListsView({ reload }) {
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/admin/telepro/lists/history", { params: { limit: 60 } });
      setHistory(r.data.items || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/telepro/lists/generate", {});
      if (r.data.error === "no_active_telepros") {
        toast.error("Aucun(e) téléprospecteur/trice actif/ve. Crée et active l'équipe d'abord.");
      } else if (r.data.already_generated) {
        toast.message(`Listes du ${r.data.date} déjà générées.`, { description: `${r.data.lists.length} listes existantes.` });
      } else {
        toast.success(`Listes générées : ${r.data.total_assigned} entreprises sur ${r.data.lists?.length || 0} personnes.`);
      }
      loadHistory();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const sendAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/telepro/lists/send");
      const sent = (r.data.results || []).filter(x => x.status === "sent").length;
      const errs = (r.data.results || []).filter(x => x.status !== "sent").length;
      toast.success(`Emails : ${sent} envoyés, ${errs} en erreur/manquants.`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const resendAll = async () => {
    const ok = window.confirm(
      "Renvoyer les emails du jour à TOUTES les téléprospectrices ?\n\n" +
      "Utile si elles disent ne pas avoir reçu leur liste (souvent en spam).\n" +
      "Les listes déjà envoyées ce matin seront ré-expédiées avec un nouvel ID Resend."
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/telepro/lists/resend");
      const sent = (r.data.results || []).filter(x => x.status === "sent").length;
      const skipped = (r.data.results || []).filter(x => x.status !== "sent").length;
      toast.success(`Renvoi effectué : ${sent} email(s) partis, ${skipped} en attente/erreur.`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const downloadZip = async () => {
    try {
      const r = await api.get("/admin/telepro/lists/today/zip", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `listes-prospection-${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      // axios with responseType=blob returns the error body as a Blob too
      let msg = "Téléchargement impossible";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          msg = (JSON.parse(txt).detail) || msg;
        }
      } catch { /* ignore */ }
      toast.error(msg);
    }
  };

  const downloadOne = async (id, code) => {
    try {
      const r = await api.get(`/admin/telepro/lists/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `liste-${code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Erreur PDF");
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-muted-foreground">
            Génère les listes du jour pour toute ton équipe active, puis envoie chaque PDF par email.
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={generate} disabled={busy} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="telepro-generate-btn">
              <ArrowClockwise size={14} className={busy ? "animate-spin" : ""}/> Générer les listes du jour
            </button>
            <button onClick={sendAll} disabled={busy} className="bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-3 py-2 rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="telepro-send-btn">
              <PaperPlaneTilt size={14}/> Envoyer par email
            </button>
            <button onClick={resendAll} disabled={busy} className="bg-[#C8795A] text-white hover:bg-[#a55f45] px-3 py-2 rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="telepro-resend-btn" title="Force le renvoi des emails du jour aux 4 télépros — pratique si elles disent ne pas avoir reçu leur liste">
              <PaperPlaneTilt size={14}/> Renvoyer aux 4 télépros
            </button>
            <button onClick={downloadZip} className="bg-accent hover:bg-accent/70 px-3 py-2 rounded-sm text-sm font-semibold inline-flex items-center gap-2" data-testid="telepro-zip-btn">
              <FileZip size={14}/> ZIP du jour
            </button>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-accent/20">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Personne</th>
              <th className="px-3 py-2 text-right">Entreprises</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {history.map(li => (
              <tr key={li.id} data-testid={`list-row-${li.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{li.date}</td>
                <td className="px-3 py-2 font-mono text-primary text-xs font-bold">{li.telepro_code}</td>
                <td className="px-3 py-2">{li.telepro_name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{li.count}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase tracking-widest font-bold ${
                    li.email_status === "sent" ? "text-primary" :
                    li.email_status === "error" ? "text-destructive" :
                    li.email_status === "missing_email" ? "text-amber-600" : "text-muted-foreground"
                  }`}>{li.email_status === "sent" ? "Envoyé" : li.email_status === "error" ? "Erreur" : li.email_status === "missing_email" ? "Email manquant" : "En attente"}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => downloadOne(li.id, li.telepro_code)} className="p-1.5 hover:bg-accent rounded-sm" title="PDF"><Download size={14}/></button>
                </td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr><td colSpan="6" className="px-3 py-10 text-center text-sm text-muted-foreground">Aucune liste générée. Clique &laquo; Générer les listes du jour &raquo;.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Sales ----------
function TeleproSalesView({ telepros }) {
  const [sales, setSales] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ telepro_id: "", plan: "premium", business_name: "", owner_name: "", phone: "", email: "" });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/telepro/sales");
      setSales(r.data.items || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.telepro_id || !form.business_name) {
      toast.error("Téléprospecteur/trice et nom entreprise obligatoires");
      return;
    }
    try {
      await api.post("/admin/telepro/sales", form);
      toast.success("Vente enregistrée");
      setAdding(false);
      setForm({ telepro_id: "", plan: "premium", business_name: "", owner_name: "", phone: "", email: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Marquer cette vente comme résiliée ? Les commissions récurrentes s'arrêtent.")) return;
    try {
      await api.patch(`/admin/telepro/sales/${id}`, { status: "cancelled" });
      toast.success("Vente résiliée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {sales.length} vente(s) · {sales.filter(s => s.status === "active").length} active(s)
        </div>
        <button onClick={() => setAdding(!adding)} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-sm font-semibold inline-flex items-center gap-2" data-testid="telepro-add-sale-btn">
          <Plus size={14} weight="bold"/> Ajouter une vente
        </button>
      </div>

      {adding && (
        <div className="bg-card border-2 border-primary/40 rounded-sm p-5">
          <div className="grid md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Téléprospecteur/trice *</div>
              <select value={form.telepro_id} onChange={e => setForm({...form, telepro_id: e.target.value})}
                      className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm" data-testid="sale-telepro-select">
                <option value="">— Choisir —</option>
                {telepros.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.code} · {t.first_name} {t.last_name}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Type d&apos;abonnement *</div>
              <select value={form.plan} onChange={e => setForm({...form, plan: e.target.value})}
                      className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm" data-testid="sale-plan-select">
                {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label} (vente {v.sale} · mensuel {v.monthly})</option>)}
              </select>
            </label>
            <Input label="Nom entreprise *" value={form.business_name} onChange={v => setForm({...form, business_name: v})} testid="sale-biz-name"/>
            <Input label="Nom dirigeant" value={form.owner_name} onChange={v => setForm({...form, owner_name: v})} testid="sale-owner-name"/>
            <Input label="Téléphone" value={form.phone} onChange={v => setForm({...form, phone: v})} testid="sale-phone"/>
            <Input label="Email" type="email" value={form.email} onChange={v => setForm({...form, email: v})} testid="sale-email"/>
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={() => setAdding(false)} className="px-4 py-2 text-sm hover:bg-accent rounded-sm">Annuler</button>
            <button onClick={submit} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2 text-sm font-semibold rounded-sm" data-testid="sale-submit-btn">Enregistrer la vente</button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-accent/20">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Téléprospecteur</th>
              <th className="px-3 py-2">Entreprise</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2 text-right">Vente</th>
              <th className="px-3 py-2 text-right">Mensuel</th>
              <th className="px-3 py-2 text-center">Statut</th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sales.map(s => (
              <tr key={s.id} data-testid={`sale-row-${s.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{(s.sale_date || "").slice(0, 10)}</td>
                <td className="px-3 py-2 font-mono text-primary text-xs font-bold">{s.telepro_code}</td>
                <td className="px-3 py-2">{s.business_name}</td>
                <td className="px-3 py-2 text-xs">{PLAN_LABELS[s.plan]?.label}</td>
                <td className="px-3 py-2 text-right tabular-nums">{s.sale_commission_eur}€</td>
                <td className="px-3 py-2 text-right tabular-nums">{s.monthly_commission_eur}€/mois</td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${s.status === "active" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{s.status === "active" ? "Actif" : "Résilié"}</span>
                </td>
                <td className="px-3 py-2">
                  {s.status === "active" && <button onClick={() => cancel(s.id)} className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-sm" title="Résilier"><X size={14}/></button>}
                </td>
              </tr>
            ))}
            {sales.length === 0 && (
              <tr><td colSpan="8" className="px-3 py-10 text-center text-sm text-muted-foreground">Aucune vente enregistrée pour le moment.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Commissions ----------
// ---------- Monthly comptable export (VAGUE 20) ----------
function MonthlyCommissionExporter() {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [busy, setBusy] = useState(null);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const url = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/admin/telepro/commissions/${year}/${String(month).padStart(2, "0")}/export.${fmt}`;
      const token = localStorage.getItem("token");
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { toast.error("Erreur téléchargement"); return; }
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `commissions-${year}-${String(month).padStart(2, "0")}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error("Erreur téléchargement");
    } finally { setBusy(null); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        value={month}
        onChange={(e) => setMonth(parseInt(e.target.value))}
        className="text-sm bg-background border border-border rounded-sm px-2 py-1"
        data-testid="export-month-select"
      >
        {["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"].map((m, i) => (
          <option key={i+1} value={i+1}>{m}</option>
        ))}
      </select>
      <input
        type="number"
        value={year}
        onChange={(e) => setYear(parseInt(e.target.value))}
        className="text-sm bg-background border border-border rounded-sm px-2 py-1 w-24"
        min="2024" max="2100"
        data-testid="export-year-input"
      />
      <button
        onClick={() => download("csv")}
        disabled={!!busy}
        className="btn-secondary btn-sm"
        data-testid="export-csv-btn"
      >
        <Download size={14}/> CSV
      </button>
      <button
        onClick={() => download("pdf")}
        disabled={!!busy}
        className="btn-primary btn-sm"
        data-testid="export-pdf-btn"
      >
        <FilePdf size={14}/> PDF
      </button>
    </div>
  );
}

function TeleproCommissionsView({ telepros }) {  const [global, setGlobal] = useState(null);
  const [running, setRunning] = useState(false);
  const [details, setDetails] = useState({}); // telepro_id -> dashboard

  const loadGlobal = useCallback(async () => {
    try {
      const r = await api.get("/admin/telepro/dashboard");
      setGlobal(r.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadGlobal(); }, [loadGlobal]);

  const loadDetails = async (tid) => {
    if (details[tid]) {
      const next = {...details}; delete next[tid]; setDetails(next); return;
    }
    try {
      const r = await api.get(`/admin/telepro/${tid}/dashboard`);
      setDetails({...details, [tid]: r.data});
    } catch { /* noop */ }
  };

  const runMonthly = async () => {
    if (!window.confirm("Lancer le cycle de commissions mensuelles ? (idempotent — n'écrase pas un mois déjà payé)")) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/telepro/commissions/run-monthly");
      toast.success(`Cycle terminé : ${r.data.paid_count} commissions stampées, ${r.data.total_eur}€ ce mois`);
      loadGlobal();
      setDetails({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setRunning(false); }
  };

  if (!global) return <div className="text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div className="space-y-4">
      {/* VAGUE 20 — Export comptable mensuel CSV/PDF */}
      <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-4 dark:bg-emerald-950/20">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-base font-semibold text-emerald-900 dark:text-emerald-200 flex items-center gap-2">
              📊 Export comptable mensuel
            </div>
            <div className="text-xs text-emerald-800 dark:text-emerald-300 mt-1">
              Télécharge le récap comptable HT / TVA / TTC de toute l'équipe en un clic — prêt à envoyer à ton comptable.
            </div>
          </div>
          <MonthlyCommissionExporter />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Téléprospecteurs/trices" value={global.telepros_active}/>
        <Stat label="Ventes totales" value={global.sales_total} sub={`${global.sales_active} actives · ${global.sales_cancelled} résiliées`}/>
        <Stat label="Commissions vente cumulées" value={`${global.total_sale_commissions_eur}€`} accent/>
        <Stat label="Mensuels payés cumulés" value={`${global.total_monthly_commissions_paid_eur}€`} accent/>
        <Stat label="Ce mois (payé)" value={`${global.this_month_commissions_paid_eur}€`}/>
        <Stat label="Prochain run mensuel" value={`${global.next_monthly_run_total_eur}€`}/>
        <Stat label="Grand total payé" value={`${global.grand_total_paid_eur}€`} accent/>
        <button onClick={runMonthly} disabled={running} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white p-4 rounded-sm text-sm font-semibold inline-flex flex-col items-center justify-center gap-1 disabled:opacity-60" data-testid="telepro-run-monthly-btn">
          <CurrencyEur size={18}/>
          {running ? "En cours…" : "Lancer le cycle mensuel"}
        </button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-3 py-2 text-xs uppercase tracking-widest font-bold text-muted-foreground border-b border-border bg-accent/20">
          Détail par personne
        </div>
        <div className="divide-y divide-border">
          {telepros.map(tp => {
            const det = details[tp.id];
            return (
              <div key={tp.id}>
                <button onClick={() => loadDetails(tp.id)} className="w-full px-3 py-2 hover:bg-accent/20 text-left text-sm flex justify-between items-center" data-testid={`telepro-detail-${tp.code}`}>
                  <span>
                    <span className="font-mono text-primary text-xs font-bold">{tp.code}</span> · {tp.first_name} {tp.last_name}
                  </span>
                  <span className="text-xs text-muted-foreground">{det ? "▲ replier" : "▼ déplier"}</span>
                </button>
                {det && (
                  <div className="px-3 pb-4 grid grid-cols-2 md:grid-cols-4 gap-2 bg-accent/10">
                    <MiniStat label="Ventes" value={det.sales_count}/>
                    <MiniStat label="Actives" value={det.active_count}/>
                    <MiniStat label="Vente cumulé" value={`${det.total_sale_commissions_eur}€`}/>
                    <MiniStat label="Mensuel cumulé" value={`${det.total_monthly_commissions_eur}€`}/>
                    <MiniStat label="Ce mois" value={`${det.this_month_commissions_eur}€`}/>
                    <MiniStat label="Total" value={`${det.grand_total_eur}€`}/>
                    <MiniStat label="Listes" value={det.lists_count}/>
                    <MiniStat label="Contacts estim." value={det.total_contacted_estimated}/>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className={`p-3 rounded-sm border ${accent ? "bg-primary/10 border-primary/30" : "bg-card border-border"}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className="font-display text-xl font-medium tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-sm px-2 py-2 text-center">
      <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
      <div className="font-display text-sm tabular-nums font-medium">{value}</div>
    </div>
  );
}
